import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { SERVICES, SERVICE_IMAGES, SPECIALTY_DELIVERIES } from "../i18n";
import { ArrowLeft, ArrowRight, Check, Plus, Tag, Gift } from "lucide-react";
import { toast } from "sonner";
import VoiceBookingButton from "../components/VoiceBookingButton";
import TextBookingButton from "../components/TextBookingButton";
import AvailabilityCalendar from "../components/AvailabilityCalendar";

// Resolve a service name from either main services or specialty deliveries
function getServiceName(t, key) {
  return t.services[key]?.name || t.specialty[key]?.name || key;
}

export default function Booking() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    service: "",
    date: "",
    time: "",
    address: "",
    phone: "",
    details: "",
    name: user?.name || "",
    email: user?.email || "",
    referral_code: "",
    discount_code: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [referralStatus, setReferralStatus] = useState(null); // {valid, referrer_name, discount_eur} | null
  const [codePanelOpen, setCodePanelOpen] = useState(false);
  const [subLabel, setSubLabel] = useState(""); // Optional pre-selected sub-service label from /book?sub=...
  const [bookingMethod, setBookingMethod] = useState("manual"); // voice | text | sub | manual — first one set sticks

  // Auto-load referral code from URL ?ref=TAL-XXXXXX, discount code from localStorage,
  // and an optional sub-service label from ?sub=... so we can show a banner
  // ("Sie haben 'Friedhofspflege' gewählt") and prefill the details textarea.
  useEffect(() => {
    const url = new URL(window.location.href);
    const refCode = url.searchParams.get("ref") || localStorage.getItem("talkani_ref_code") || "";
    if (refCode) {
      setData((d) => ({ ...d, referral_code: refCode.toUpperCase() }));
      localStorage.setItem("talkani_ref_code", refCode.toUpperCase());
      setCodePanelOpen(true);
    }
    // Sub-service deep-link (e.g. /book?service=gartenpflege&sub=Friedhofspflege ...)
    const sub = url.searchParams.get("sub");
    if (sub) {
      const decoded = decodeURIComponent(sub).slice(0, 180);
      setSubLabel(decoded);
      setBookingMethod((m) => (m === "manual" ? "sub" : m));
      setData((d) => ({
        ...d,
        details: d.details
          ? d.details
          : `Bezugnehmend auf: ${decoded}\n\n`,
      }));
    }
    // Exit-intent discount code (set by ExitIntentPopup)
    if (localStorage.getItem("talkani_exit_intent_seen_v1") === "1") {
      // Pre-fill suggested code if user copied it
      setData((d) => ({ ...d, discount_code: d.discount_code || "" }));
    }
  }, []);

  // Scroll to top whenever the user navigates between booking steps so the new
  // step heading ("Datum & Zeit", "Adresse & Kontakt", …) is immediately visible
  // on mobile — otherwise the page stays scrolled to where the previous step's
  // bottom buttons were and the user has to manually scroll up.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Use 'auto' (instant) so the user sees the new step right away — smooth
    // scrolling feels sluggish on multi-step forms.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [step]);

  // Validate referral code when it changes
  useEffect(() => {
    const code = (data.referral_code || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      setReferralStatus(null);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/referrals/validate/${code}`);
        setReferralStatus(r.data);
      } catch {
        setReferralStatus({ valid: false });
      }
    }, 400);
    return () => clearTimeout(id);
  }, [data.referral_code]);

  const canNext = () => {
    if (step === 1) return !!data.service;
    if (step === 2) return !!data.date && !!data.time;
    if (step === 3) {
      const baseOk = !!data.name && !!data.email && !!data.address && !!data.phone;
      // For "andere" (custom service), require a description in details field
      if (data.service === "andere") return baseOk && !!data.details && data.details.trim().length > 5;
      return baseOk;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Use guest endpoint if not logged in, authenticated endpoint if logged in
      const endpoint = user ? "/bookings" : "/bookings/guest";
      const payload = {
        ...data,
        booking_method: bookingMethod,
        ...(subLabel ? { sub_label: subLabel } : {}),
      };
      const r = await api.post(endpoint, payload);
      setDone(r.data);
    } catch (e) {
      toast.error("Error");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="w-16 h-16 bg-[#FF5A00] text-white rounded-full flex items-center justify-center mx-auto mb-6">
          <Check size={32} />
        </div>
        <h1 className="text-4xl font-black tracking-tighter mb-3" data-testid="booking-success-title">{t.booking.success}</h1>
        <p className="text-neutral-600 mb-2">{t.booking.success_msg}</p>
        <p className="text-xs font-bold tracking-widest uppercase text-neutral-500 mt-4 mb-8">#{done.booking_id}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/track?id=${done.booking_id}&email=${encodeURIComponent(data.email)}`} className="btn-primary inline-flex" data-testid="booking-track-btn">
            {t.track?.track_btn || "Track"} <ArrowRight size={18} />
          </Link>
          <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-neutral-300 hover:border-[#FF5A00] hover:text-[#FF5A00] font-bold tracking-wider uppercase text-xs transition" data-testid="booking-home-btn">
            {t.nav.home}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
      <div className="mb-10">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">{t.booking.step} {step} {t.booking.of} 4</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter" data-testid="booking-title">{t.booking.title}</h1>
        <Stepper step={step} t={t} />
      </div>

      <div className="bg-white border border-neutral-200 p-8 lg:p-10">
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold mb-2">{t.booking.step1}</h2>
            {subLabel && (
              <div className="bg-orange-50 border-l-4 border-[#FF5A00] px-4 py-3 mb-5 flex items-start gap-3" data-testid="booking-sub-banner">
                <Check size={18} className="text-[#FF5A00] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-widest uppercase text-[#FF5A00] mb-0.5">Ihre Auswahl</div>
                  <div className="font-bold text-neutral-900 text-sm">{subLabel}</div>
                  <div className="text-xs text-neutral-500 mt-1">In den Details bereits vermerkt. Sie können den Text unten anpassen.</div>
                </div>
              </div>
            )}
            <VoiceBookingButton
              onFields={(f) => {
                const next = { ...data };
                if (f.service) next.service = f.service;
                if (f.date) next.date = f.date;
                if (f.time) next.time = f.time;
                if (f.address) next.address = f.address;
                if (f.details) next.details = f.details;
                setData(next);
                setBookingMethod("voice");
                if (f.service) toast.success("Service automatisch gewählt");
              }}
            />
            <TextBookingButton
              onFields={(f) => {
                const next = { ...data };
                if (f.service) next.service = f.service;
                if (f.date) next.date = f.date;
                if (f.time) next.time = f.time;
                if (f.address) next.address = f.address;
                if (f.details) next.details = f.details;
                setData(next);
                setBookingMethod("text");
                if (f.service) toast.success("Service automatisch gewählt");
              }}
            />
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4 mt-2">{t.booking.step1_main}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SERVICES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setData({ ...data, service: s })}
                  className={`text-start border-2 p-5 transition-all ${data.service === s ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 hover:border-neutral-400"}`}
                  data-testid={`service-option-${s}`}
                >
                  <div className="aspect-video bg-neutral-100 mb-4 overflow-hidden">
                    <img src={SERVICE_IMAGES[s]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="font-bold text-sm">{t.services[s].name}</div>
                </button>
              ))}
            </div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4 mt-10">{t.booking.step1_specialty}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SPECIALTY_DELIVERIES.map(({ key, image, isCustom }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setData({ ...data, service: key })}
                  className={`text-start border-2 p-3 transition-all ${data.service === key ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 hover:border-neutral-400"}`}
                  data-testid={`specialty-option-${key}`}
                >
                  <div className={`aspect-square mb-3 overflow-hidden flex items-center justify-center ${isCustom ? "bg-gradient-to-br from-orange-100 to-orange-200" : "bg-neutral-100"}`}>
                    {isCustom ? (
                      <Plus size={48} className="text-[#FF5A00]" strokeWidth={2.5} />
                    ) : (
                      <img src={image} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="font-bold text-xs">{t.specialty[key].name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold mb-6">{t.booking.step2}</h2>
            <AvailabilityCalendar value={data.date} onChange={(d) => setData({ ...data, date: d })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <Field label={t.booking.date} type="date" value={data.date} onChange={v => setData({...data, date: v})} testid="booking-date" />
              <Field label={t.booking.time} type="time" value={data.time} onChange={v => setData({...data, time: v})} testid="booking-time" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold mb-6">{t.booking.step3}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t.booking.name} value={data.name} onChange={v => setData({...data, name: v})} testid="booking-name" />
                <Field label={t.booking.email} type="email" value={data.email} onChange={v => setData({...data, email: v})} testid="booking-email" />
              </div>
              <Field label={t.booking.address} value={data.address} onChange={v => setData({...data, address: v})} testid="booking-address" />
              <Field label={t.booking.phone} value={data.phone} onChange={v => setData({...data, phone: v})} testid="booking-phone" />
              <div>
                <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">
                  {data.service === "andere" ? `${t.booking.details_custom} *` : t.booking.details}
                </label>
                <textarea
                  rows={4}
                  value={data.details}
                  onChange={e => setData({...data, details: e.target.value})}
                  placeholder={data.service === "andere" ? t.booking.details_custom_placeholder : ""}
                  className="w-full border border-neutral-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
                  data-testid="booking-details"
                />
              </div>

              {/* Referral / Discount code */}
              <div className="border-t border-neutral-200 pt-4">
                {!codePanelOpen ? (
                  <button
                    type="button"
                    onClick={() => setCodePanelOpen(true)}
                    className="text-sm font-bold text-[#FF5A00] hover:text-[#E04F00] inline-flex items-center gap-2"
                    data-testid="booking-toggle-codes"
                  >
                    <Tag size={14} /> {t.booking.have_code}
                  </button>
                ) : (
                  <div className="space-y-3 bg-neutral-50 p-4 border border-neutral-200">
                    <div>
                      <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2 flex items-center gap-1.5">
                        <Gift size={12} /> {t.booking.referral_code_label}
                      </label>
                      <input
                        type="text"
                        value={data.referral_code}
                        onChange={(e) => setData({ ...data, referral_code: e.target.value.toUpperCase() })}
                        placeholder="TAL-XXXXXX"
                        className="w-full border border-neutral-300 px-4 py-2.5 text-sm uppercase tracking-wider focus:outline-none focus:border-[#FF5A00]"
                        data-testid="booking-referral-code"
                      />
                      {referralStatus?.valid && (
                        <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-2 border border-emerald-200" data-testid="booking-referral-valid">
                          ✓ {t.booking.referral_valid.replace("{name}", referralStatus.referrer_name).replace("{eur}", referralStatus.discount_eur)}
                        </div>
                      )}
                      {referralStatus?.valid === false && data.referral_code && (
                        <div className="mt-2 text-xs text-rose-700 bg-rose-50 px-3 py-2 border border-rose-200" data-testid="booking-referral-invalid">
                          {t.booking.referral_invalid}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2 flex items-center gap-1.5">
                        <Tag size={12} /> {t.booking.discount_code_label}
                      </label>
                      <input
                        type="text"
                        value={data.discount_code}
                        onChange={(e) => setData({ ...data, discount_code: e.target.value.toUpperCase() })}
                        placeholder="TALKANI15"
                        className="w-full border border-neutral-300 px-4 py-2.5 text-sm uppercase tracking-wider focus:outline-none focus:border-[#FF5A00]"
                        data-testid="booking-discount-code"
                      />
                    </div>
                    <p className="text-[11px] text-neutral-500 leading-relaxed">{t.booking.code_note}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="text-xl font-bold mb-6">{t.booking.step4}</h2>
            <dl className="divide-y divide-neutral-200 border border-neutral-200">
              <Row label={t.nav.services} value={getServiceName(t, data.service)} />
              <Row label={t.booking.date} value={`${data.date} · ${data.time}`} />
              <Row label={t.booking.name} value={data.name} />
              <Row label={t.booking.email} value={data.email} />
              <Row label={t.booking.address} value={data.address} />
              <Row label={t.booking.phone} value={data.phone} />
              {data.details && <Row label={t.booking.details} value={data.details} />}
            </dl>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <button
            disabled={step === 1}
            onClick={() => setStep(s => s - 1)}
            className="btn-outline text-neutral-900 disabled:opacity-30"
            data-testid="booking-back-btn"
          >
            <ArrowLeft size={18} /> {t.booking.back}
          </button>
          {step < 4 ? (
            <button
              disabled={!canNext()}
              onClick={() => setStep(s => s + 1)}
              className="btn-primary disabled:opacity-50"
              data-testid="booking-next-btn"
            >
              {t.booking.next} <ArrowRight size={18} />
            </button>
          ) : (
            <button
              disabled={submitting}
              onClick={submit}
              className="btn-primary disabled:opacity-50"
              data-testid="booking-confirm-btn"
            >
              {submitting ? t.common.loading : t.booking.confirm} <Check size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ step, t }) {
  const steps = [t.booking.step1, t.booking.step2, t.booking.step3, t.booking.step4];
  return (
    <div className="flex items-center gap-2 mt-6">
      {steps.map((s, i) => {
        const idx = i + 1;
        const active = step === idx, done = step > idx;
        return (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border-2 rounded-sm ${active || done ? "bg-[#FF5A00] text-white border-[#FF5A00]" : "bg-white text-neutral-400 border-neutral-300"}`}>
              {done ? <Check size={14} /> : idx}
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${done ? "bg-[#FF5A00]" : "bg-neutral-300"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-neutral-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
        data-testid={testid}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 px-5 py-4 text-sm">
      <dt className="font-semibold text-neutral-500 uppercase text-xs tracking-widest">{label}</dt>
      <dd className="col-span-2 font-medium">{value}</dd>
    </div>
  );
}
